import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HoursPerDay } from '@cfl/shared';

import { api, API_URL } from './api';

export interface MonthDay {
  day: string;
  expectedSeconds: number;
  actualSeconds: number;
  balanceSeconds: number;
  note: 'holiday' | 'half-day holiday' | 'absence' | 'half-day absence' | null;
  holidayName: string | null;
}

export interface MonthTotals {
  expectedSeconds: number;
  actualSeconds: number;
  balanceSeconds: number;
  /** Elapsed days only. Null when the whole period is already in the past. */
  toDate: { expectedSeconds: number; actualSeconds: number; balanceSeconds: number } | null;
  workingDays: number;
  absenceDays: number;
  holidayDays: number;
}

/** Whether this person's month has been closed, and by whom. */
export type MonthCloseState =
  | { closed: true; closedAt: string; closedBy: string; expectedSeconds: number; actualSeconds: number }
  | { closed: false; reopenedAt?: string; reopenedBy?: string | null; reopenReason?: string | null };

export interface MonthView {
  employee: { id: string; displayName: string };
  month: string;
  contract: {
    hoursPerDay: HoursPerDay;
    vacationDaysPerYear: number;
    absenceCalculationMode: 'COMPENSATE' | 'REDUCE';
    holidayGroup: { id: string; name: string } | null;
    workStartDate: string | null;
    workEndDate: string | null;
  } | null;
  days: MonthDay[];
  totals: MonthTotals | null;
  message: string | null;
  close: MonthCloseState;
}

export interface YearTotal {
  /** "2026-27" where the year does not start in January. */
  label: string;
  from: string;
  to: string;
  actualSeconds: number;
}

/**
 * Hours recorded against the current financial year.
 *
 * April to March here, from `financialYearStartMonth` - which is why this comes
 * from the API rather than being worked out in the browser from a calendar year.
 */
export const useYearTotal = (employeeId?: string) =>
  useQuery({
    queryKey: ['working-time', 'year-total', employeeId ?? 'me'],
    queryFn: () =>
      api.get<YearTotal>(
        `/working-time/year-total${employeeId ? `?employeeId=${employeeId}` : ''}`,
      ),
  });

export const useMonth = (month: string, employeeId?: string) =>
  useQuery({
    queryKey: ['working-time', 'month', month, employeeId ?? 'me'],
    queryFn: () =>
      api.get<MonthView>(
        `/working-time/month?month=${month}${employeeId ? `&employeeId=${employeeId}` : ''}`,
      ),
  });

/**
 * Closing a month raises the person's lockdown boundary to its last day, so every
 * cached view of that month is stale afterwards - hence the broad invalidation.
 */
const useMonthMutation = (path: 'close' | 'reopen') => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { employeeId?: string; month: string; reason?: string }) =>
      api.post<{ month: string }>(`/working-time/month/${path}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['working-time'] });
      queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
};

export const useCloseMonth = () => useMonthMutation('close');
export const useReopenMonth = () => useMonthMutation('reopen');

/**
 * The printable statement opens in a new tab rather than being fetched.
 *
 * It is a document, not data: the browser's own print dialog is what turns it into
 * a PDF, and fetching the HTML into this app would mean re-rendering it here.
 */
export function monthReportUrl(month: string, employeeId?: string): string {
  return `${API_URL}/working-time/month/report?month=${month}${employeeId ? `&employeeId=${employeeId}` : ''}`;
}
